import { useState } from 'react';
import Toggle from './Toggle';
import SectionHeading from '../shared/SectionHeading';
import { TEXT_STYLES, BORDER_STYLES, SPACING } from '../../constants/styles';

function ProjectResults({ title = 'Results', description, hpImage, finalDesignImages, finalDesignTitle = 'Final Design', finalDesignContent, finalDesignRows, finalDesignFooterImage }) {
  const [showNewDesign, setShowNewDesign] = useState(true);

  if (!description) return null;

  // Check if we have finalDesignImages (for Inform) or hpImage (for RIBO)
  const hasFinalDesign =
    (finalDesignRows && finalDesignRows.length > 0) ||
    (finalDesignImages && finalDesignImages.length > 0) ||
    hpImage;

  return (
    <div id="results" className={SPACING.scrollOffset}>
      <div className={`max-w-3xl mx-auto bg-[#F7F7F7] ${BORDER_STYLES.rounded} ${BORDER_STYLES.default} px-8 md:px-12 py-10 md:py-14`}>
        {title && <SectionHeading>{title}</SectionHeading>}
        <p className={`text-base md:text-lg ${TEXT_STYLES.body}`}>
          {description}
        </p>
      </div>

      {hasFinalDesign && (
        <section id="final-design" className="w-screen relative mt-8 md:mt-12 scroll-mt-32 mb-0" style={{ marginLeft: 'calc(-50vw + 50%)', marginRight: 'calc(-50vw + 50%)' }}>
          <div className={`max-w-5xl mx-auto ${SPACING.container} pt-12 md:pt-16`}>
            {/* Final Design Header */}
            <SectionHeading className="mb-8 text-center">{finalDesignTitle}</SectionHeading>
            
            {/* Show toggle only for RIBO (when hpImage exists but no finalDesignImages) */}
            {hpImage && !finalDesignImages && (
              <Toggle
                leftLabel="Old Design"
                rightLabel="New Design"
                isRight={showNewDesign}
                onChange={setShowNewDesign}
                ariaLabel="Toggle design view"
              />
            )}

            {/* Final designs in rows */}
            {finalDesignRows && finalDesignRows.length > 0 ? (
              <div className="space-y-12">
                {finalDesignRows.map((row, rowIndex) => (
                  <div key={rowIndex}>
                    <h3 className="text-xl md:text-2xl font-playfair font-normal text-[#0D0D0D] mb-6 text-center">
                      {row.title}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                      {row.images.map((image, imageIndex) => (
                        <div key={imageIndex} className="w-full">
                          <img
                            src={image}
                            alt={`${row.title} design ${imageIndex + 1}`}
                            className="w-full h-auto rounded-lg shadow-lg"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {finalDesignFooterImage && (
                  <div className="w-full">
                    <img
                      src={finalDesignFooterImage}
                      alt="Final Design"
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                  </div>
                )}
              </div>
            ) : finalDesignImages ? (
              // Show finalDesignImages - handle single image or multiple images
              finalDesignImages.length === 1 ? (
                // Single image with optional content (Pinch or Arc)
                finalDesignContent ? (
                  // Arc: Image on left, content on right
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
                    <div className="w-full md:col-span-3">
                      <img
                        src={finalDesignImages[0]}
                        alt="Final Design"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                    <div className="w-full md:col-span-1 space-y-8">
                      {finalDesignContent.filter(item => !item.imageRight).map((item, index) => (
                        <div key={index}>
                          <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-base text-[#666666] font-normal leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  // Pinch: Single image only
                  <div className="w-full">
                    <img
                      src={finalDesignImages[0]}
                      alt="Final Design"
                      className="w-full h-auto rounded-lg shadow-lg"
                    />
                  </div>
                )
              ) : finalDesignImages.length === 2 && finalDesignContent ? (
                // Arc: Multiple images with content
                <div className="space-y-16">
                  {/* First image: Tasks - Image left, content right */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
                    <div className="w-full md:col-span-3">
                      <img
                        src={finalDesignImages[0]}
                        alt="Final Design"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                    <div className="w-full md:col-span-1 space-y-8">
                      {finalDesignContent.filter(item => !item.imageRight).map((item, index) => (
                        <div key={index}>
                          <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                            {item.title}
                          </h3>
                          <p className="text-base text-[#666666] font-normal leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Second image: Profiles - Content left, image right */}
                  {finalDesignContent.filter(item => item.imageRight).map((item, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-6 md:gap-8 items-center">
                      <div className="w-full md:col-span-1">
                        <h3 className="text-lg md:text-xl font-medium text-[#0D0D0D] mb-2">
                          {item.title}
                        </h3>
                        <p className="text-base text-[#666666] font-normal leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                      <div className="w-full md:col-span-3">
                        <img
                          src={finalDesignImages[1]}
                          alt="Final Design"
                          className="w-full h-auto rounded-lg shadow-lg"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // Multiple images (Inform)
                <div>
                  {/* First row: Dashboard with text beside it */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-24 items-center">
                    <div className="w-full">
                      <img
                        src={finalDesignImages[0]}
                        alt="Inform Dashboard"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                    <div className="w-full pl-0 md:pl-8">
                      <p className="text-base md:text-lg text-[#666666] font-normal leading-relaxed">
                        <span className="font-medium text-[#0D0D0D]">Inform does it all!</span>
                        <br />
                        Access your reviews and reply to them directly from the Inform dashboard
                      </p>
                    </div>
                  </div>
                  {/* Second row: Weekly Summary with text on the left */}
                  <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-24 items-center">
                    <div className="w-full pl-0 md:pl-8">
                      <p className="text-base md:text-lg text-[#666666] font-normal leading-relaxed">
                        <span className="font-medium text-[#0D0D0D]">Weekly insights at a glance</span>
                        <br />
                        Track your review performance and sentiment trends over time
                      </p>
                    </div>
                    <div className="w-full">
                      <img
                        src={finalDesignImages[1]}
                        alt="Inform Weekly Summary"
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  </div>
                  {/* Third row: Landing, Blog, Membership */}
                  <div className="mt-24">
                    <h3 className="text-xl md:text-2xl font-playfair font-normal text-[#0D0D0D] mb-6 md:mb-8 text-center">
                      Inform Website
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                    {finalDesignImages.slice(2, 5).map((image, index) => {
                      const imageNames = ['Landing', 'Blog', 'Membership'];
                      return (
                        <div key={index} className="w-full">
                          <img
                            src={image}
                            alt={`Inform ${imageNames[index] || 'Design'}`}
                            className="w-full h-auto rounded-lg shadow-lg"
                          />
                        </div>
                      );
                    })}
                    </div>
                  </div>
                </div>
              )
            ) : (
              // RIBO: Show with toggle in one row
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                <div className="w-full">
                  <img
                    src={showNewDesign ? (hpImage || '/ribo/RIBO - HP.png') : '/ribo/RIBO - Old HP.png'}
                    alt={showNewDesign ? 'RIBO Homepage' : 'RIBO Old Homepage'}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
                <div className="w-full">
                  <img
                    src={showNewDesign ? '/ribo/RIBO - Contact.png' : '/ribo/RIBO - Old Contact.png'}
                    alt={showNewDesign ? 'RIBO Contact' : 'RIBO Old Contact'}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
                <div className="w-full">
                  <img
                    src={showNewDesign ? '/ribo/RIBO - Team.png' : '/ribo/RIBO - Old Team.png'}
                    alt={showNewDesign ? 'RIBO Team' : 'RIBO Old Team'}
                    className="w-full h-auto rounded-lg shadow-lg"
                  />
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default ProjectResults;
